import type { MouseEvent } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LocalizationLanguage, useAppContext } from '@gov-nx/core/app';
import { useAppLanguage } from '@gov-nx/core/hooks';
import { scrollToTop } from '@gov-nx/web';
import { Button } from '../Button/Button';
import { Icon } from '../Icon';

export interface LanguageSwitchProps {
	inDropdown?: boolean;
	onClick?: () => void;
}

export const LanguageSwitchLink = ({
	inDropdown,
	onClick,
}: LanguageSwitchProps) => {
	const { t } = useTranslation();
	const { setLoading } = useAppContext();
	const { language, toggle } = useAppLanguage();

	const langSwitchTranslationKeySuffix =
		language === LocalizationLanguage.Czech ? 'english' : 'cesky';

	const onSwitchLanguage = async (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		setLoading(true);
		await toggle();
		onClick?.();
	};

	const langSwitchFlagIcon =
		language === LocalizationLanguage.Czech ? (
			<Icon
				name="flag-eng"
				type="colored"
			/>
		) : (
			<Icon
				name="flag-cze"
				type="colored"
			/>
		);

	return inDropdown ? (
		<>
			<Button
				size="l"
				color="primary"
				type="base"
				isExpanded
				className="gov-button-text-left nav-primary--link__secondary [ ds:hidden! ]"
				onClick={async (event) => {
					await onSwitchLanguage(event);
					scrollToTop();
				}}
				aria-label={t(
					`portal-obcana.navigace.wcag.${langSwitchTranslationKeySuffix}`
				)}
				title={t(
					`portal-obcana.navigace.wcag.${langSwitchTranslationKeySuffix}`
				)}
				iconStart={langSwitchFlagIcon}>
				{t(`portal-obcana.navigace.${langSwitchTranslationKeySuffix}`)}
			</Button>
			<Button
				size="s"
				color="neutral"
				type="base"
				isExpanded
				className="gov-button-text-left nav-primary--link__secondary hidden! [ ds:inline-flex! ]"
				onClick={async (event) => {
					await onSwitchLanguage(event);
					scrollToTop();
				}}
				aria-label={t(
					`portal-obcana.navigace.wcag.${langSwitchTranslationKeySuffix}`
				)}
				title={t(
					`portal-obcana.navigace.wcag.${langSwitchTranslationKeySuffix}`
				)}>
				{langSwitchFlagIcon}
				{t(`portal-obcana.navigace.${langSwitchTranslationKeySuffix}`)}
			</Button>
		</>
	) : (
		<Button
			size="m"
			color="primary"
			type="base"
			onClick={async (event) => {
				await onSwitchLanguage(event);
			}}
			aria-label={t(
				`portal-obcana.navigace.wcag.${langSwitchTranslationKeySuffix}`
			)}
			title={t(
				`portal-obcana.navigace.wcag.${langSwitchTranslationKeySuffix}`
			)}>
			{langSwitchFlagIcon}
			{t(`portal-obcana.navigace.${langSwitchTranslationKeySuffix}`)}
		</Button>
	);
};
